/**
 * Leitura do catálogo (`GET /catalog`). Puro: quem chama passa o catálogo
 * (nos componentes, `useCatalog()`).
 *
 * O catálogo diz o que cada tipo de passo é: rótulo, se está "Em breve",
 * as saídas (`PortsSpec`) e o config de um passo novo. Cor, ícone e
 * família continuam no front (`constants/node-families.ts`), porque são
 * apresentação.
 */

import { nanoid } from 'nanoid';
import type {
  CatalogNode,
  CatalogPortDef,
  CatalogStatus,
  PortsSpec,
  WorkflowCatalog,
} from '../api/types';
import {
  FAMILY_META,
  NODE_FAMILY,
  isKnownNodeKind,
} from '../constants/node-families';
import type { FamilyMeta, NodeFamily } from '../constants/node-families';
import type { EdgeStyle, NodeKind, TrackOutput } from '../types';

/* ----- Tipos de passo ----- */

export const catalogNode = (
  catalog: WorkflowCatalog,
  kind: string,
): CatalogNode | undefined => catalog.nodes.find((n) => n.kind === kind);

/** Nome do tipo de passo ("Envio"). Tipo fora do catálogo: o próprio id. */
export const kindLabel = (catalog: WorkflowCatalog, kind: string): string =>
  catalogNode(catalog, kind)?.label ?? kind;

/**
 * Dá para adicionar e editar: está no catálogo, não é "Em breve" e o front
 * tem formulário para ele. O resto aparece como "Em breve" (paleta) ou em
 * leitura (painel).
 */
export const isKindAvailable = (
  catalog: WorkflowCatalog,
  kind: string,
): boolean => {
  const node = catalogNode(catalog, kind);
  return Boolean(node) && !node?.disabled && isKnownNodeKind(kind);
};

/**
 * Saiu da paleta e do menu rápido (`deprecated`): quem já tem o passo
 * continua editando, mas não dá para pôr um novo.
 */
export const isKindDeprecated = (
  catalog: WorkflowCatalog,
  kind: string,
): boolean => Boolean(catalogNode(catalog, kind)?.deprecated);

/** O passo de envio do perfil (o catálogo já vem filtrado por ele). */
export const dispatchKindOf = (catalog: WorkflowCatalog): 'disp' | 'offer' =>
  catalogNode(catalog, 'disp') || !catalogNode(catalog, 'offer')
    ? 'disp'
    : 'offer';

/* ----- Portas de saída ----- */

export type PortVariant = 'default' | 'alt' | 'fail' | 'muted';

export interface NodePort {
  id: string;
  /** Com rótulo, a porta é uma pill nomeada; sem, o "+" de saída única. */
  label?: string;
  variant: PortVariant;
  /** O que faz o pedido sair por ela (ajuda do passo, tooltip da porta). */
  description?: string;
}

const VARIANTS: ReadonlySet<string> = new Set([
  'default',
  'alt',
  'fail',
  'muted',
]);

/** Só põe `description` quando há texto (catálogo antigo não manda). */
const withDescription = (port: NodePort, description?: string): NodePort =>
  description ? { ...port, description } : port;

const toPort = (p: CatalogPortDef): NodePort =>
  withDescription(
    {
      id: p.id,
      label: p.label || undefined,
      variant: VARIANTS.has(p.variant ?? '')
        ? (p.variant as PortVariant)
        : 'default',
    },
    p.description,
  );

/** Lê `a.b.c` num objeto qualquer. */
export function readPath(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === 'object'
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      obj,
    );
}

/**
 * Portas de um config segundo o `PortsSpec`, na ordem do card. `fromList`
 * gera uma porta por item (id em `idKey`, rótulo em `labelKey`, descrição
 * em `itemDescription`) e põe a `fallback` no fim. Item sem id não vira
 * porta. `whenUsed` mostra, na ordem de `items`, as portas de `always` e as
 * que algum item da lista usa em `key`.
 */
export function portsFromSpec(spec: PortsSpec, config: unknown): NodePort[] {
  if (spec.type === 'static') return spec.items.map(toPort);
  if (spec.type === 'whenUsed') {
    const list = readPath(config, spec.path);
    const used = new Set<unknown>(spec.always ?? []);
    for (const item of Array.isArray(list) ? list : []) {
      used.add(((item ?? {}) as Record<string, unknown>)[spec.key]);
    }
    return spec.items.filter((p) => used.has(p.id)).map(toPort);
  }
  const list = readPath(config, spec.path);
  const ports: NodePort[] = [];
  for (const item of Array.isArray(list) ? list : []) {
    const row = (item ?? {}) as Record<string, unknown>;
    const id = row[spec.idKey];
    if (typeof id !== 'string' || !id) continue;
    const label = spec.labelKey ? row[spec.labelKey] : undefined;
    ports.push(
      withDescription(
        {
          id,
          label: typeof label === 'string' && label ? label : undefined,
          variant: 'default',
        },
        spec.itemDescription,
      ),
    );
  }
  if (spec.fallback) ports.push(toPort(spec.fallback));
  return ports;
}

const SINGLE_OUT: NodePort[] = [{ id: 'out', variant: 'default' }];

/** O que as consultas de porta leem de um passo. */
export interface NodeLike {
  kind: string;
  config: unknown;
}

/**
 * Saídas que um passo tem hoje. Tipo fora do catálogo (grafo antigo, outro
 * perfil): a saída única padrão.
 */
export function outputPorts(
  catalog: WorkflowCatalog,
  node: NodeLike,
): NodePort[] {
  const spec = catalogNode(catalog, node.kind)?.ports;
  return spec ? portsFromSpec(spec, node.config) : SINGLE_OUT;
}

export const outputPortIds = (
  catalog: WorkflowCatalog,
  node: NodeLike,
): string[] => outputPorts(catalog, node).map((p) => p.id);

/**
 * Todas as saídas que o tipo pode ter (a ajuda do passo explica todas):
 * em `whenUsed`, também as que nenhuma regra usa ainda. Nos outros tipos,
 * as de hoje.
 */
export function possibleOutputPorts(
  catalog: WorkflowCatalog,
  node: NodeLike,
): NodePort[] {
  const spec = catalogNode(catalog, node.kind)?.ports;
  return spec?.type === 'whenUsed'
    ? spec.items.map(toPort)
    : outputPorts(catalog, node);
}

/**
 * Porta por onde o fluxo "segue" num passo recém-criado: a primeira saída
 * (a única, Saiu do status, Quando ocorrer, o Senão numa Condição sem
 * casos).
 */
export const primaryOutputPortId = (
  catalog: WorkflowCatalog,
  node: NodeLike,
): string => outputPorts(catalog, node)[0]?.id ?? 'out';

/** A porta `fallback` de um `fromList` (o Senão da Condição), se houver. */
export function fallbackPort(
  catalog: WorkflowCatalog,
  kind: string,
): NodePort | null {
  const spec = catalogNode(catalog, kind)?.ports;
  return spec?.type === 'fromList' && spec.fallback
    ? toPort(spec.fallback)
    : null;
}

/**
 * Estilo da ligação que sai de uma porta: a cor da porta. `alt` (âmbar,
 * tracejada) e `fail` (roxa, tracejada) passam para a ligação; o resto é a
 * linha cheia padrão.
 */
export function edgeStyleForPort(
  catalog: WorkflowCatalog,
  node: NodeLike,
  portId: string,
): EdgeStyle {
  const variant = outputPorts(catalog, node).find(
    (p) => p.id === portId,
  )?.variant;
  return variant === 'alt' || variant === 'fail' ? variant : 'default';
}

/* ----- Config de um passo novo ----- */

const clone = <T>(value: T): T =>
  value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);

/** Dá `id` aos itens de uma lista que chegam sem (o catálogo não gera). */
function ensureIds(list: unknown, idKey: string, makeId: () => string) {
  if (!Array.isArray(list)) return;
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    if (!row[idKey]) row[idKey] = makeId();
  }
}

/**
 * Config de um passo novo: o `defaultConfig` do catálogo (cópia), com os
 * ids que só o front gera — itens que viram porta (casos da Condição) e as
 * linhas do Atualizar dados.
 */
export function defaultConfigFor(
  catalog: WorkflowCatalog,
  kind: string,
): Record<string, unknown> {
  const def = catalogNode(catalog, kind);
  const config = clone(def?.defaultConfig ?? {});
  const spec = def?.ports;
  if (spec?.type === 'fromList') {
    ensureIds(readPath(config, spec.path), spec.idKey, () => `c_${nanoid(6)}`);
  }
  if (kind === 'update-fields') {
    ensureIds(config['updates'], 'id', () => nanoid(6));
  }
  return config;
}

/* ----- Status ----- */

/** Código completo do sub-status (o catálogo já manda `STATUS:SUB`). */
const subCode = (s: CatalogStatus, subId: string) =>
  subId.includes(':') ? subId : `${s.id}:${subId}`;

const subCodes = (s: CatalogStatus): string[] =>
  (s.subStatuses ?? []).map((sub) => subCode(s, sub.id));

/**
 * Verificar status: a lista do catálogo para o seletor (`statusScopes`).
 * Sem ela, os sub-status dos status que não são finais.
 */
export const inactivityStatusCodes = (catalog: WorkflowCatalog): string[] =>
  catalog.statusScopes?.inactivity ??
  catalog.statuses.filter((s) => !s.terminal).flatMap(subCodes);

/**
 * Aguardar evento: a lista do catálogo para o seletor (`statusScopes`), já
 * pelo perfil (conta transportadora tem "Nenhum entregador disponível").
 * Sem ela, as falhas na solicitação do envio.
 */
export const eventStatusCodes = (catalog: WorkflowCatalog): string[] =>
  catalog.statusScopes?.resend ??
  catalog.statuses.filter((s) => s.id === 'ORDER_FAILED').flatMap(subCodes);

/** Os sub-status do status; sem sub-status, o próprio status (`CANCELED`). */
const codesOf = (s: CatalogStatus): string[] =>
  s.subStatuses?.length ? subCodes(s) : [s.id];

/** Status de cada saída do Acompanhar entrega quando o catálogo não manda. */
const TRACK_FALLBACK_PARENTS: Record<TrackOutput, string[] | null> = {
  stuck: null,
  failed: ['ORDER_FAILED', 'FAILED', 'CANCELED', 'MANUAL_HANDLE', 'RETURNED'],
  out: ['SUCCESSFUL'],
};

/**
 * Acompanhar entrega: os status que uma regra de cada saída pode escolher
 * (`statusScopes.track`, já pelo perfil). Sem ela: Parado usa os do
 * Verificar status; Falha, as falhas, Cancelado, Baixa manual e
 * Devolvido; Sucesso, os sub-status de Sucesso.
 */
export function trackStatusCodes(
  catalog: WorkflowCatalog,
  output: TrackOutput,
): string[] {
  const scoped = catalog.statusScopes?.track?.[output];
  if (scoped) return scoped;
  const parents = TRACK_FALLBACK_PARENTS[output];
  if (!parents) return inactivityStatusCodes(catalog);
  return parents.flatMap((id) => {
    const s = catalog.statuses.find((x) => x.id === id);
    return s ? codesOf(s) : [];
  });
}

/** Status pai e sub-status de um código `STATUS` ou `STATUS:SUB`. */
export function findStatus(
  catalog: WorkflowCatalog,
  code: string,
): { status?: CatalogStatus; sub?: { id: string; label: string } } {
  const [parentId] = code.split(':');
  const status = catalog.statuses.find((s) => s.id === parentId);
  const sub =
    status && code.includes(':')
      ? status.subStatuses?.find((x) => subCode(status, x.id) === code)
      : undefined;
  return { status, sub };
}

/**
 * Nome de um código para chips e cards: "Despachado — Buscando entregador";
 * status sem sub-status, só o nome. Código fora do catálogo, o próprio
 * código.
 */
export function statusLabel(catalog: WorkflowCatalog, code: string): string {
  const { status, sub } = findStatus(catalog, code);
  if (!code.includes(':')) return status?.label ?? code;
  const [parentId, subId] = code.split(':');
  return `${status?.label ?? parentId} — ${sub?.label ?? subId}`;
}

/**
 * Nome curto, para resumos de uma linha: só o sub-status ("Buscando
 * entregador"); status sem sub-status, o nome dele ("Cancelado").
 */
export function statusShortLabel(
  catalog: WorkflowCatalog,
  code: string,
): string {
  const { status, sub } = findStatus(catalog, code);
  if (!code.includes(':')) return status?.label ?? code;
  return sub?.label ?? code.split(':')[1];
}

/* ----- Família (cor e seção da paleta) ----- */

/** A família do catálogo; sem ela (ou desconhecida), a do front. */
export function familyOfKind(
  catalog: WorkflowCatalog,
  kind: string,
): NodeFamily {
  const fromCatalog = catalogNode(catalog, kind)?.family;
  if (fromCatalog && fromCatalog in FAMILY_META)
    return fromCatalog as NodeFamily;
  return NODE_FAMILY[kind as NodeKind] ?? 'notify';
}

/** Cor e nome da família do tipo. */
export const familyMetaOf = (
  catalog: WorkflowCatalog,
  kind: string,
): FamilyMeta => FAMILY_META[familyOfKind(catalog, kind)];
